import { useEffect } from "react";
import Shepherd from "shepherd.js";
// The Home tour's placement: 12px from the target and from the screen edges, clamped
// to the layout viewport (so a rotation can't leave the WebView zoomed out), with the
// arrow re-aimed at the target after the clamp.
import { TOOLTIP_PLACEMENT } from "../Home/HomeWalkthrough";

const TOTAL = 4;

function makeText(text, idx) {
  const dots = Array.from({ length: TOTAL }, (_, i) =>
    `<span class="hw-dot${i === idx ? " hw-dot-active" : ""}"></span>`
  ).join("");
  return `<p style="margin:0">${text}</p><div class="hw-dots">${dots}</div>`;
}

const Walkthrough = ({ onDone }) => {
  useEffect(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: false },
        modalOverlayOpeningRadius: 14,
        modalOverlayOpeningPadding: 8,
        scrollTo: false,
        ...TOOLTIP_PLACEMENT,
      },
    });

    const skipBtn = { text: "Skip", action: () => tour.cancel(), classes: "hw-btn-secondary" };
    const nextBtn = (label = "Next →") => ({ text: label, action: () => tour.next(), classes: "hw-btn-primary" });

    // Step 0: page already at top before tour starts — no scroll needed
    tour.addStep({
      id: "bookmark",
      title: "Bookmark this root",
      text: makeText("Save it to Bookmarks for quick access. Find all your saved roots in the tab bar.", 0),
      attachTo: { element: "#wt-bookmark", on: "bottom" },
      buttons: [skipBtn, nextBtn()],
    });

    // Steps 1-3: scroll element to center BEFORE Shepherd renders the spotlight.
    // Using 'before-show' (fires before setupForStep) + instant scroll keeps
    // getBoundingClientRect() in sync when Shepherd measures the position.
    [
      { id: "flashcard", target: "#wt-flashcard", on: "top", title: "Add to flashcards", body: "Quick-add to your default deck. The deck icon lets you choose a specific collection." },
      { id: "copy",      target: "#wt-copy",      on: "top", title: "Copy definition",   body: "Copies the word and its full definition text to your clipboard." },
      { id: "toggle",    target: "#wt-toggle",    on: "top", title: "Short or long",      body: "Switch between the condensed summary and the full dictionary entry." },
    ].forEach(({ id, target, on, title, body }, i) => {
      tour.addStep({
        id,
        title,
        text: makeText(body, i + 1),
        attachTo: { element: target, on },
        when: {
          "before-show"() {
            const el = document.querySelector(target);
            if (el) el.scrollIntoView({ behavior: "instant", block: "center" });
          },
        },
        buttons: [skipBtn, nextBtn(i === 2 ? "Done" : "Next →")],
      });
    });

    // Swipe left → next, swipe right → back
    let touchStartX = 0;
    let touchStartY = 0;
    const onTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        if (dx < 0) tour.next();
        else tour.back();
      }
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });

    tour.on("cancel", onDone);
    tour.on("complete", onDone);
    tour.start();

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
      tour.off("cancel");
      tour.off("complete");
      try { tour.cancel(); } catch (_) {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default Walkthrough;
